import { Card, CardContent } from '@ui/card.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { unparse } from 'papaparse';
import { companiesImportBody } from '@r/api/generated-zod/index.ts';
import { useFileParserWithErrors } from '@r/hooks/use-file-parser-patch-with-errors.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { Trans, useTranslation } from 'react-i18next';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Button } from '@r/components/ui/button.tsx';
import { Factory, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateNewCompanyDialog } from '@admin/components/company/create-new-company-dialog.tsx';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { useContext } from 'react';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import { z } from 'zod';

const COMMENT_MAX_LENGTH = 255;

// Le séparateur "|" permet de rattacher plusieurs commerciaux à une même société.
const sampleRows = [
  { uniqueCode: 'FIDDY1234', city: 'Paris', responsiblesUniqueCodes: 'RESP001' },
  { uniqueCode: 'FIDDY1235', city: 'Lyon', responsiblesUniqueCodes: 'RESP001|RESP002' },
  {
    uniqueCode: 'FIDDY1236',
    city: 'Marseille',
    responsiblesUniqueCodes: 'RESP001|RESP002|RESP003'
  },
  { uniqueCode: 'FIDDY1237', city: 'Toulouse', responsiblesUniqueCodes: '' },
  { uniqueCode: 'FIDDY1238', city: 'Nantes', responsiblesUniqueCodes: 'RESP004|RESP005' }
];

export default function CompaniesImport() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { state, setErrorState, setPreviewState, resetState } = useImportState('companies');

  // Utiliser le schéma Zod généré par Orval pour les données d'import d'entreprises
  // Le schéma companiesImportBody a la structure { data: [...] }
  // Chaque item du tableau correspond à une ligne CSV
  const companiesImportSchema = companiesImportBody.shape.data.element.extend({
    comment: z
      .string()
      .max(COMMENT_MAX_LENGTH, t('company.comment.max.length', { max: COMMENT_MAX_LENGTH }))
      .nullish()
  });
  const { onParse } = useFileParserWithErrors(companiesImportSchema, '/admin/companies/import');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const isTypology = isTypologyConversion(workspaceConfigContext);

  const fields = Object.keys(companiesImportSchema.shape).filter((field) =>
    isTypology ? field !== 'pointCoeff' : field !== 'typologyName'
  );

  const csv = unparse({
    fields,
    data: sampleRows.map(({ uniqueCode, city, responsiblesUniqueCodes }) => [
      'Fiddy',
      uniqueCode,
      '123456789',
      'TVA FR 123456789',
      '40 Rue du louvre',
      '',
      '',
      '75000',
      city,
      'FRA',
      responsiblesUniqueCodes,
      isTypology ? 'Default' : 1.5,
      'Commentaire exemple'
    ])
  });

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_companies_import_example.csv`
    : 'companies_import_example.csv';

  const handleParse = async (files: File[]) => {
    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="companies"
        analysis={state.analysis}
      />
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.companies.import.title')}</h1>

      <CreateNewCompanyDialog isOpen={isModalOpen} onOpenChange={setIsModalOpen} />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <Factory className="w-6 h-6" />
              <p className="text-xl font-bold">{t('company.create.title')}</p>
            </h2>
            <p className="text-muted-foreground">{t('company.create.description')}</p>
          </div>
        </CardContent>

        <CardContent className="flex grow">
          <Button onClick={() => setIsModalOpen(true)} rounded="full">
            {t('company.create.button')} <Plus />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <Factory className="w-6 h-6" />
              <p className="text-xl font-bold">{t('companies.import.title')}</p>
            </h2>

            <p className="text-muted-foreground">
              <Trans i18nKey="companies.import.description" components={{ 1: <br /> }} />
            </p>

            <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />
          </div>

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>
    </div>
  );
}
